import React, { useState } from 'react';
import { Item as ShareFileItem } from '@citrite/sf-api';
import {
	FormSlideColorOnWhite,
	PointIconsColorOnWhite,
} from '@sharefiledev/branding-assets';
import { Card, Flex, notification, theme, Typography, Upload, UploadFile } from 'antd';
import { RcFile } from 'antd/es/upload';
import { useAppStore } from '../../common/store';
import { DocumentTemplateTypes, MAX_FILE_SIZE } from '../../constants';
import { DocgenClient } from '../../data/docgenClient';
import { handleErrorWithServerMessage } from '../../data/errorHandler';
import { CopyFileFromSharefilePayload } from '../../data/interfaces/DocumentTemplateFile';
import { FileChooser } from '../../pages/Start/FileChooser';
import { ProgressState } from '../../pages/Start/SelectDocument/types';
import { t } from '../../utils';

const { Dragger } = Upload;
const { Text } = Typography;

interface CustomFileUploaderProps {
	setUploadState: (state: ProgressState) => void;
	resetUploadState: () => void;
}

interface UploadCardContentProps {
	icon: React.ReactNode;
	text: React.ReactNode;
	onClick?: () => void;
}

const UploadCardContent: React.FC<UploadCardContentProps> = ({ icon, text, onClick }) => {
	const { token } = theme.useToken();

	return (
		<Flex
			vertical
			justify="space-between"
			align="center"
			style={{ height: '100%' }}
			onClick={onClick}
		>
			{icon}
			<div>
				<Text style={{ fontSize: token.fontSizeLG }}>{text}</Text>
			</div>
		</Flex>
	);
};

const uploadCardStyles = { width: 231, height: 246 };

export const FileUploadComponent = ({
	setUploadState,
	resetUploadState,
}: CustomFileUploaderProps) => {
	const [isSfFilePickerVisible, setIsSfFilePickerVisible] =
		React.useState<boolean>(false);
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const { token } = theme.useToken();

	const { fetchThumbnailUrl, documentTemplate, updateDocumentTemplate } = useAppStore(
		state => ({
			fetchThumbnailUrl: state.fetchThumbnailUrl,
			updateDocumentTemplate: state.updateDocumentTemplate,
			documentTemplate: state.documentTemplate,
		})
	);

	const onSfFilePickerClose = (file: ShareFileItem) => {
		setIsSfFilePickerVisible(false);
		if (file !== null) {
			uploadFromSharefile(file);
		}
	};

	const uploadFromSharefile = async (file: ShareFileItem) => {
		setUploadState('started');

		const payload: CopyFileFromSharefilePayload = {
			fileName: file.FileName,
			fileId: file.Id,
			overwrite: true,
		};
		try {
			const response = await DocgenClient.copyFileFromSharefile(
				documentTemplate?.rid,
				payload
			);
			if (response !== null) {
				setUploadState('storing');
				completeUploadAndFetchThumbnail(file.FileName);
			}
		} catch {
			handleErrorWithServerMessage(t('errorMessages.uploadFileError'));
			resetUploadState();
		}
	};

	const updateFileName = (fileName: string) => {
		updateDocumentTemplate({ file: { ...documentTemplate.file, fileName } });
	};

	const completeUploadAndFetchThumbnail = (fileName: string) => {
		try {
			fetchThumbnailUrl(documentTemplate?.rid);
			updateFileName(fileName);
			setUploadState('ready');
		} catch {
			handleErrorWithServerMessage(t('errorMessages.fetchThumbnailUrlError'));
			resetUploadState();
		}
	};

	const handleChange = info => {
		if (info.file.status === 'done') {
			const file = fileList[0];
			uploadFromComputer(file);
		}
	};

	const uploadFromComputer = async file => {
		setUploadState('started');

		const formData = new FormData();
		formData.append('content', file);

		try {
			const response = await DocgenClient.createFileUpload(
				formData,
				documentTemplate?.rid
			);

			if (response === '') {
				setUploadState('storing');
				completeUploadAndFetchThumbnail(file.name);
			}
		} catch {
			handleErrorWithServerMessage(t('errorMessages.uploadFileError'));
			resetUploadState();
		}
	};

	const validateFile = (file: File) => {
		const isDocx =
			file.type ===
			'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
		const isValidSize = file.size <= MAX_FILE_SIZE;

		if (!isDocx) {
			notification.error({
				message: 'Error',
				description: <span>{t('uploadDocxFileModal.fileTypeErrorNotification')}</span>,
			});
		}

		if (!isValidSize) {
			notification.error({
				message: 'Error',
				description: <span>{t('maxFileSizeLimitMessage')}</span>,
			});
		}

		return isDocx && isValidSize;
	};

	const handleBeforeUpload = (file: RcFile) => {
		if (validateFile(file)) {
			setFileList(prevList => [...prevList, file]);
			return true;
		}
		return false;
	};

	const handleDrop = (e: React.DragEvent) => {
		e.stopPropagation();
		if (fileList[0]?.size <= MAX_FILE_SIZE) {
			setFileList([]);
		}
	};

	return (
		<>
			<Flex
				align="center"
				justify="center"
				gap={token.marginMD}
				style={{ marginBottom: token.marginXXL }}
			>
				<div onDragOver={e => e.stopPropagation()} style={uploadCardStyles}>
					<Dragger
						name="file-uploader"
						accept={DocumentTemplateTypes.Dynamic.supportedFileExtensions.join(',')}
						multiple={false}
						beforeUpload={handleBeforeUpload}
						onChange={handleChange}
						onDrop={handleDrop}
						fileList={fileList}
						data-testid="file-uploader"
						style={{
							paddingTop: token.paddingMD,
							paddingBottom: token.paddingSM,
							background: 'unset',
						}}
					>
						<UploadCardContent
							icon={<PointIconsColorOnWhite width={105} />}
							text={
								<>
									{t('uploadDocxFileModal.uploadTypes.computer.dragFilesHereOr')}{' '}
									<span style={{ color: token.colorPrimary }}>
										{t('uploadDocxFileModal.uploadTypes.computer.browse')}
									</span>
								</>
							}
						/>
					</Dragger>
				</div>

				<Card
					onClick={() => setIsSfFilePickerVisible(true)}
					style={uploadCardStyles}
					styles={{
						body: {
							height: '100%',
						},
					}}
				>
					<UploadCardContent
						icon={<FormSlideColorOnWhite width={147} />}
						text={t('uploadDocxFileModal.uploadTypes.sharefile')}
					/>
				</Card>
			</Flex>
			{isSfFilePickerVisible && (
				<FileChooser
					onClose={onSfFilePickerClose}
					templateType={DocumentTemplateTypes.Dynamic.type}
				/>
			)}
		</>
	);
};
